import type { JSX } from 'react'
import { hideTenant, restoreTenant } from '../api.js'
import { pathFor } from '../routing.js'
import { HiddenNote, HideButton, canHide } from './hidden.jsx'
import { NewCustomer } from './new-customer.jsx'
import { QrveyBadge } from './account-settings.jsx'
import type { Me, TenantSummary } from '../api.js'

/**
 * The customer list.
 *
 * Whatever the API returned, and nothing more. A scoped engineer's list is
 * filtered server-side by `visibleTenants`, so this does no filtering of its own
 * — a second filter here would be a place for the two to disagree, and the one
 * that matters is the one an attacker cannot skip.
 */
export const Customers = ({
  baseUrl,
  me,
  tenants,
  onOpen,
  onChanged,
  showHidden,
  onShowHidden,
}: {
  readonly baseUrl: string
  readonly me: Me
  readonly tenants: readonly TenantSummary[]
  readonly onOpen: (path: string) => void
  /** Reloads the list, so a customer just created appears without a refresh. */
  readonly onChanged: () => void
  readonly showHidden: boolean
  readonly onShowHidden: (on: boolean) => void
}): JSX.Element => (
  <>
    <section className="panel">
      <h2>Customers</h2>
      {/*
        Off by default — a shorter list is the whole point. Offered only to
        someone who could act on what it reveals, because a toggle that shows
        rows with no control beside them is a puzzle rather than a feature.
      */}
      {canHide(me.role) ? (
        <label className="toggle">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(event) => onShowHidden(event.target.checked)}
          />{' '}
          Show hidden
        </label>
      ) : null}
      {tenants.length === 0 ? (
        <p role="status">
          {me.scope.kind === 'tenants' && me.scope.tenantIds.length === 0
            ? 'You have no customers assigned yet. An administrator can assign them.'
            : 'No customers have been registered yet.'}
        </p>
      ) : (
        <ul className="cards">
          {tenants.map((tenant) => (
            <li key={tenant.tenantId}>
              <a
                href={pathFor({ screen: 'customer', tenantId: tenant.tenantId })}
                onClick={(event) => {
                  // Left-click navigates in place; anything with a modifier is
                  // left alone so middle-click and cmd-click still open a tab.
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                  event.preventDefault()
                  onOpen(pathFor({ screen: 'customer', tenantId: tenant.tenantId }))
                }}
              >
                {tenant.name}
              </a>{' '}
              <QrveyBadge kind={tenant.kind} />
              <p className="muted">{tenant.tenantId}</p>
              {tenant.deletedAt === undefined ? null : (
                <HiddenNote at={tenant.deletedAt} by={tenant.deletedBy} />
              )}
              <HideButton
                role={me.role}
                hidden={tenant.deletedAt !== undefined}
                what="customer"
                name={tenant.name}
                confirm
                onHide={() => hideTenant(baseUrl, tenant.tenantId)}
                onRestore={() => restoreTenant(baseUrl, tenant.tenantId)}
                onChanged={onChanged}
              />
            </li>
          ))}
        </ul>
      )}
      <NewCustomer baseUrl={baseUrl} role={me.role} onCreated={onChanged} />
    </section>
  </>
)
